/**
 * useHeroPastLife — TanStack Query v5 polling hook for the hero past-life text.
 *
 * Endpoint: GET /api/v1/me/past-life-hero?lang=<lang>
 * Response: { ready: true; markdown: string } | { ready: false }
 *
 * Polls every 2.5s until ready (same pattern as useNatalReading).
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';

export type HeroPastLifeResponse =
  | { ready: true; markdown: string }
  | { ready: false };

interface UseHeroPastLifeResult {
  data: HeroPastLifeResponse | undefined;
  /** Convenience: true only when data.ready === true */
  ready: boolean;
  /** Convenience: markdown string when ready, undefined otherwise */
  markdown: string | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function useHeroPastLife(enabled: boolean): UseHeroPastLifeResult {
  const jwt = useAuth((s) => s.jwt);
  const { i18n } = useTranslation();

  const query = useQuery<HeroPastLifeResponse, Error>({
    queryKey: ['past-life-hero', i18n.language],
    queryFn: async () => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await apiFetch(`/api/v1/me/past-life-hero?lang=${i18n.language}`, jwt);
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`past_life_hero_failed: ${res.status} ${body}`);
      }
      return (await res.json()) as HeroPastLifeResponse;
    },
    enabled: !!jwt && enabled,
    staleTime: 5 * 60_000,
    retry: false,
    // Poll every 2.5s until ready — used while the server generates the hero text.
    refetchInterval: (q): number | false => {
      const d = q.state.data as HeroPastLifeResponse | undefined;
      return d?.ready === true ? false : 2500;
    },
  });

  const ready = query.data?.ready === true;
  const markdown = ready ? (query.data as { ready: true; markdown: string }).markdown : undefined;

  return {
    data: query.data,
    ready,
    markdown,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
